import React from "react";
import { useTranslation } from "react-i18next";

type BulkActionsBarProps = {
  /** Whether bulk-select mode is on. */
  bulkMode: boolean;
  /** Toggle the mode. Exiting always clears the selection. */
  onToggleMode: () => void;
  /** How many photos are currently selected. Zero disables the actions. */
  selectedCount: number;
  onMarkBlank: () => void;
  onAcceptPredictions: () => void;
  onAddObservation: () => void;
  onPointToSpecies: () => void;
  onClear: () => void;
  /** True while a bulk action is committing — disables everything. */
  busy?: boolean;
  /**
   * The toggle button, so the page can put focus here when leaving the mode —
   * the tile checkbox that had focus unmounts, and this is somewhere
   * deliberate to send it.
   */
  toggleRef?: React.RefObject<HTMLButtonElement>;
};

/**
 * The feed's bulk-select control and actions bar (AKL-753, fourth action
 * AKL-811).
 *
 * The Bulk Select button is always visible: it is how the feature is
 * discoverable at all, and it doubles as the mode indicator. Turning the mode on
 * puts a checkbox on every tile and makes a plain click select rather than open
 * the photo; the actions appear alongside it and stay disabled until something
 * is selected, so the bar never offers an action that would do nothing.
 *
 * Four actions today: Mark as blank, Accept AI Predictions, Add observation and
 * Point to one species. Only Mark as blank carries the orange destructive
 * styling — in this bar orange means "discards existing observations", which is
 * true of it and of nothing else. Point to one species explicitly leaves saved
 * observations alone, and its undo-less framing belongs in its modal, where the
 * reviewer has something to read.
 *
 * Modelled on the AI Mappings label table's selection bar, minus two things it
 * has deliberately:
 *
 * - **No select-all.** Photos are hand-picked one at a time. That bounds every
 *   batch to what a reviewer actually looked at, which is what removes the scale
 *   problem behind AKL-751 by construction rather than by capping it.
 * - **No "hidden selected" count.** The mappings table keeps its selection across
 *   filter changes and so has to warn when it is about to act on rows the view is
 *   hiding. The feed clears its selection on any page or filter change, so a
 *   photo can never be selected and out of sight at the same time.
 *
 * Purely presentational: no state, no requests — the page owns the mode, the
 * confirmations and the commits.
 */
const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  bulkMode,
  onToggleMode,
  selectedCount,
  onMarkBlank,
  onAcceptPredictions,
  onAddObservation,
  onPointToSpecies,
  onClear,
  busy = false,
  toggleRef,
}) => {
  const { t } = useTranslation();
  const nothingSelected = selectedCount === 0;

  // No wrapper margins and no section heading: this renders inline on the queue
  // header's density row, and the Bulk Select button names the thing and shows
  // its own state, so a label above it would only repeat itself.
  return (
    <div
      className="flex flex-row flex-wrap items-center gap-4 bg-sky-blue/10 rounded px-4 py-2"
      data-testid="review-bulk-actions"
    >
      <button
        ref={toggleRef}
        type="button"
        className={`flex items-center gap-1.5 rounded px-2 py-1 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none disabled:opacity-50 ${
          bulkMode
            ? "bg-sky-blue text-storm-blue"
            : "text-sky-blue-dark dark:text-sky-blue hover:underline"
        }`}
        onClick={onToggleMode}
        disabled={busy}
        // The button is the mode, so announce its state rather than relying on
        // the styling alone.
        aria-pressed={bulkMode}
        // The keycap below is a visual hint; this is what actually exposes the
        // shortcut to assistive tech.
        aria-keyshortcuts="s"
        data-testid="bulk-select-toggle"
      >
        <span>{t("Bulk Select")}</span>
        {/* aria-hidden so the accessible name stays "Bulk Select" rather than
            "Bulk Select S" — the shortcut is announced via aria-keyshortcuts. */}
        <kbd
          aria-hidden="true"
          className="rounded bg-black/20 px-1.5 py-0.5 text-[10px] font-mono"
        >
          S
        </kbd>
      </button>

      {bulkMode && (
        <>
          <span className="text-sm font-semibold text-storm-blue dark:text-sky-blue-light">
            {t("{{count}} photos selected", { count: selectedCount })}
          </span>
          <button
            type="button"
            className="text-primary-orange-dark hover:underline text-sm font-semibold disabled:opacity-50"
            onClick={onMarkBlank}
            disabled={busy || nothingSelected}
            data-testid="bulk-mark-blank"
          >
            {t("Mark as blank")}
          </button>
          <button
            type="button"
            className="text-sky-blue-dark dark:text-sky-blue hover:underline text-sm font-semibold disabled:opacity-50"
            onClick={onAcceptPredictions}
            disabled={busy || nothingSelected}
            data-testid="bulk-accept-predictions"
          >
            {t("Accept AI Predictions")}
          </button>
          <button
            type="button"
            className="text-sky-blue-dark dark:text-sky-blue hover:underline text-sm font-semibold disabled:opacity-50"
            onClick={onAddObservation}
            disabled={busy || nothingSelected}
            data-testid="bulk-add-observation"
          >
            {t("Add observation")}
          </button>
          <button
            type="button"
            className="text-sky-blue-dark dark:text-sky-blue hover:underline text-sm font-semibold disabled:opacity-50"
            onClick={onPointToSpecies}
            disabled={busy || nothingSelected}
            data-testid="bulk-point-to-species"
          >
            {t("Point to one species")}
          </button>
          <button
            type="button"
            className="ml-auto text-xs text-greyed-text hover:underline disabled:opacity-50"
            onClick={onClear}
            disabled={busy || nothingSelected}
          >
            {t("Clear selection")}
          </button>
        </>
      )}
    </div>
  );
};

export default BulkActionsBar;
